import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Link, Menu, MenuItem, Snackbar, Typography } from "@mui/material";
import { RiArrowDownSLine, RiArrowLeftLine, RiCheckboxCircleLine } from "@remixicon/react";
import {
  getBookingById,
  getTicketById,
  getTicketMessages,
  getUsersByIds,
  resolveTicket,
  sendTicketMessage,
  setTicketStatus,
  uploadTicketAttachment,
} from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { logUserActivity } from "../../utils/activityLogger";
import { STATUS_LABELS } from "../../utils/ticketContract";
import { statusChip, ticketNumberLabel } from "../../utils/inboxPresentation";
import {
  canResolve,
  moveOptions,
  personCards,
  raiserSide,
  reportCard,
  splitOpening,
  ticketMeta,
  ticketTitle,
  ticketTone,
  timelineEvents,
} from "../../utils/ticketDetail";
import { tokens } from "../../utils/figmaTokens";
import { useBreadcrumbName } from "../../components/Layout/BreadcrumbContext";
import { useUsersByIds } from "../Students/tabs/useUsersByIds";
import SendNotificationDialog from "../Students/SendNotificationDialog";
import { KindTile } from "./TicketParts";
import ReportCard from "./ReportCard";
import ConversationCard from "./ConversationCard";
import ResolutionCard from "./ResolutionCard";
import { EvidenceCard, PersonCard, TimelineCard } from "./SideCards";

// The ticket page (047 D54–D63), drawn from Figma's Support ticket page (Admin · Page Head ·
// Ticket 10829:93565): the back link, the head, then what was reported, the conversation and the
// resolution on the left, and the lesson, the people and the timeline on the right. Everything is
// stored data; times are the reader's. VIEW_INBOX opens the page (App.js); REPLY_INBOX writes.
const TicketPage = () => {
  const { ticketId } = useParams();
  const { user } = useAuth();
  // The whole user, so a person's grants count as well as their role (044 D5): a moderator with
  // the notifications grant sees Send notification.
  const can = (p) => hasPermission(user, p);
  const canReply = can(permissions.REPLY_INBOX);

  const [ticket, setTicket] = useState(null);
  const [missing, setMissing] = useState(false);
  const [messages, setMessages] = useState({ list: [], loading: true, error: "" });
  const [booking, setBooking] = useState(undefined); // undefined while it is read, null when it cannot be
  const [people, setPeople] = useState({});
  const [moveAnchor, setMoveAnchor] = useState(null);
  const [moving, setMoving] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const ticketToken = useRef(0);
  const messagesToken = useRef(0);
  const panelRef = useRef(null);
  const reasonRef = useRef(null);
  const titleRef = useRef(null);

  const notify = useCallback((message, severity = "success") => setSnack({ open: true, message, severity }), []);
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // The newest read wins. A refresh that fails keeps what is on screen; only a first read can end
  // in "Ticket not found." (getTicketById answers null rather than throwing).
  const load = useCallback(async () => {
    const token = ++ticketToken.current;
    const found = await getTicketById(ticketId);
    if (token !== ticketToken.current) return;
    if (found) setTicket(found);
    setMissing(!found);
  }, [ticketId]);

  const loadMessages = useCallback(async () => {
    const token = ++messagesToken.current;
    setMessages((m) => ({ ...m, loading: true, error: "" }));
    try {
      const list = await getTicketMessages(ticketId);
      if (token === messagesToken.current) setMessages({ list, loading: false, error: "" });
    } catch (e) {
      console.error("Error loading messages:", e);
      if (token === messagesToken.current) setMessages((m) => ({ ...m, loading: false, error: e.message || "Failed to load messages." }));
    }
  }, [ticketId]);

  useEffect(() => {
    setTicket(null);
    setMissing(false);
    setPeople({});
    setBooking(undefined);
    setMessages({ list: [], loading: true, error: "" });
    load();
    loadMessages();
  }, [load, loadMessages]);

  // The lesson, for a ticket about one.
  const bookingId = ticket?.bookingId;
  useEffect(() => {
    if (!bookingId) return undefined;
    let cancelled = false;
    setBooking(undefined);
    getBookingById(bookingId).then((found) => !cancelled && setBooking(found));
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  // The people the ticket names, read again with every read of the ticket, so a credit shows in
  // the wallet row at once. The senders' names come with the thread.
  useEffect(() => {
    if (!ticket) return undefined;
    let cancelled = false;
    getUsersByIds([ticket.raisedBy, ticket.studentId, ticket.teacherId, ticket.openedBy, ticket.resolvedBy])
      .then((found) => !cancelled && setPeople(found))
      .catch((e) => console.error("Error loading people:", e));
    return () => {
      cancelled = true;
    };
  }, [ticket]);
  const senders = useUsersByIds(messages.list.map((m) => m.senderId));
  const users = useMemo(() => ({ ...senders, ...people }), [senders, people]);

  // The top bar's "Support / Inbox / #12" (D54); "Not found" once the id is (D83).
  useBreadcrumbName(ticketId, ticket ? ticketNumberLabel(ticket).label : missing ? "Not found" : null);

  if (!ticket) {
    return missing ? (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          Ticket not found.
        </Alert>
        <Button component={RouterLink} to="/inbox" sx={{ mt: 2 }}>
          Back to Inbox
        </Button>
      </Box>
    ) : (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const label = ticketNumberLabel(ticket).label;
  const actor = () => ({
    userId: user?.uid || user?.id || "unknown",
    userName: user?.name || user?.displayName || "Unknown User",
    userEmail: user?.email || "unknown@example.com",
  });

  // The activity log keeps the rows the Inbox wrote before (D63).
  const moveTo = async (status) => {
    setMoveAnchor(null);
    setMoving(true);
    try {
      await setTicketStatus({ ticketId: ticket.id, status });
      // Shown at once, as the old Inbox did: a refresh that fails (getTicketById then answers
      // null) must not leave the old status and its buttons on screen.
      setTicket((t) => (t ? { ...t, status } : t));
      // Keyboard focus goes to the title, next to the new status: the button that started the
      // move can go with the old status (Request info, or Move to once a ticket is closed).
      titleRef.current?.focus();
      const who = actor();
      await logUserActivity({
        ...who,
        action: `TICKET_${status.toUpperCase()}`,
        entityType: "ticket",
        entityId: ticket.id,
        entityName: label,
        before: { status: ticket.status },
        after: { status },
        description: `${who.userName} moved ticket ${label} to ${STATUS_LABELS[status]}`,
      });
      notify(`Moved to ${STATUS_LABELS[status]}.`);
      await load();
    } catch (e) {
      notify(e.message || "Failed to update the ticket.", "error");
    } finally {
      setMoving(false);
    }
  };

  const sendReply = async (text, file) => {
    try {
      const attachmentPath = file ? await uploadTicketAttachment(ticket.id, file) : undefined;
      await sendTicketMessage(ticket.id, text, attachmentPath);
      const who = actor();
      await logUserActivity({
        ...who,
        action: "TICKET_REPLY",
        entityType: "ticket",
        entityId: ticket.id,
        entityName: label,
        description: `${who.userName} replied on ticket ${label}`,
      });
      notify("Reply sent.");
      loadMessages();
      load();
      return true;
    } catch (e) {
      notify(e.message || "Failed to send message.", "error");
      return false;
    }
  };

  const resolve = async ({ text, credit }) => {
    try {
      const payload = { ticketId: ticket.id, resolutionText: text };
      if (credit) payload.creditSessions = credit;
      const result = await resolveTicket(payload);
      // Shown at once, for the same reason; resolveTicket answers with the stored resolution.
      const resolution = result?.resolution || { text, ...(credit ? { creditSessions: credit } : {}) };
      setTicket((t) =>
        t ? { ...t, status: "resolved", resolvedBy: user?.uid || user?.id, resolvedAt: new Date(), resolution } : t
      );
      titleRef.current?.focus(); // as after a move: the card's Resolve ticket goes once it is resolved
      const who = actor();
      await logUserActivity({
        ...who,
        action: "TICKET_RESOLVED",
        entityType: "ticket",
        entityId: ticket.id,
        entityName: label,
        before: { status: ticket.status },
        after: { status: "resolved" },
        description: `${who.userName} resolved ticket ${label}${credit ? ` and credited ${credit} session(s)` : ""}`,
      });
      notify("Ticket resolved.");
      await load();
      return "";
    } catch (e) {
      return e.message || "Failed to resolve ticket.";
    }
  };

  // The head's Resolve ticket takes staff to the card (D59).
  const goToResolution = () => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    reasonRef.current?.focus({ preventScroll: true });
  };

  const tone = ticketTone(ticket);
  const chip = statusChip(ticket.status);
  const moves = canReply ? moveOptions(ticket) : [];
  const resolvable = canReply && canResolve(ticket);
  const { opening, thread } = splitOpening(ticket, messages.list);
  const raiser = ticket.raisedBy ? users[ticket.raisedBy] : null;
  const showResolution = resolvable || ticket.status === "resolved" || ticket.status === "closed";
  const person = Boolean(ticket.raisedBy) && canReply;

  return (
    <Box
      sx={{
        bgcolor: tokens.surface.default,
        border: `1px solid ${tokens.border.subtle}`,
        borderRadius: "16px",
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* The back link always opens the list (D58); the browser's Back returns where staff came from. */}
      <Link
        component={RouterLink}
        to="/inbox"
        underline="hover"
        sx={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: "6px", typography: "bodySmallStrong", color: tokens.brand.text }}
      >
        <RiArrowLeftLine size={16} aria-hidden />
        Inbox
      </Link>

      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
        <Box sx={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <KindTile tone={tone.tone} icon={tone.icon} />
            <Typography ref={titleRef} tabIndex={-1} variant="titleMedium" sx={{ color: tokens.text.primary, overflowWrap: "anywhere" }}>
              {ticketTitle(ticket, booking)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <Chip label={chip.label} color={chip.color} />
          </Box>
          <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
            {ticketMeta(ticket, users)}
          </Typography>
        </Box>
        {(moves.length > 0 || resolvable) && (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            {moves.length > 0 && (
              <>
                <Button
                  id="ticket-move-button"
                  size="large"
                  variant="outlined"
                  endIcon={<RiArrowDownSLine size={20} />}
                  onClick={(e) => setMoveAnchor(e.currentTarget)}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(moveAnchor)}
                  aria-controls={moveAnchor ? "ticket-move-menu" : undefined}
                >
                  Move to
                </Button>
                {/* A running move turns the items off, not the button: a disabled button cannot take
                    focus back when the menu closes. */}
                <Menu
                  id="ticket-move-menu"
                  anchorEl={moveAnchor}
                  open={Boolean(moveAnchor)}
                  onClose={() => setMoveAnchor(null)}
                  slotProps={{ list: { "aria-labelledby": "ticket-move-button" } }}
                >
                  {moves.map((m) => (
                    <MenuItem key={m.status} onClick={() => moveTo(m.status)} disabled={moving}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            {resolvable && (
              <Button size="large" variant="contained" startIcon={<RiCheckboxCircleLine size={20} />} onClick={goToResolution}>
                Resolve ticket
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, alignItems: { xs: "stretch", lg: "flex-start" }, gap: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          <ReportCard
            card={reportCard(ticket, { opening, users, booking })}
            tone={tone}
            pending={messages.loading && messages.list.length === 0}
            failed={Boolean(messages.error) && messages.list.length === 0}
          />
          {/* Keyed on the ticket, so a draft never follows staff to another ticket. */}
          <ConversationCard
            key={`conversation-${ticket.id}`}
            ticket={ticket}
            users={users}
            thread={thread}
            loading={messages.loading && messages.list.length === 0}
            error={messages.error}
            onRetry={loadMessages}
            canReply={canReply}
            onSend={sendReply}
            onRequestInfo={person && ticket.status === "open" ? () => moveTo("waiting_user") : undefined}
            moving={moving}
            onNotify={person && can(permissions.SEND_NOTIFICATIONS) ? () => setNotifyOpen(true) : undefined}
            notifyReady={Boolean(raiser)}
          />
          {showResolution && (
            <ResolutionCard key={`resolution-${ticket.id}`} ticket={ticket} users={users} onResolve={resolve} panelRef={panelRef} reasonRef={reasonRef} />
          )}
        </Box>

        <Box sx={{ width: { xs: "100%", lg: 419 }, flexShrink: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          {ticket.bookingId && <EvidenceCard ticket={ticket} booking={booking} canOpenLesson={can(permissions.VIEW_LESSONS)} />}
          {personCards(ticket, users).map((card) => (
            <PersonCard key={card.key} card={card} canOpen={can(card.permission)} />
          ))}
          <TimelineCard events={timelineEvents(ticket, thread, users)} />
        </Box>
      </Box>

      <SendNotificationDialog
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        person={raiser}
        noun={raiserSide(ticket, users) === "tutor" ? "tutor" : "student"}
        actingUser={user}
        onSent={(message) => notify(message)}
      />
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TicketPage;
